import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, Loader2, Check } from 'lucide-react';
import { SplaIcon } from '../components/SplaLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { sendVacancyRequest } from '../lib/sheets';

const vacancies = [
  {
    id: 1,
    title: 'Senior iOS Developer',
    department: 'Mobile Development',
    location: 'Удалённо',
    type: 'Полная занятость',
    description: 'Разработка и поддержка iOS-приложения мессенджера. Опыт с Swift, SwiftUI, работа с WebSocket и end-to-end шифрованием.',
    requirements: ['5+ лет опыта iOS разработки', 'Глубокое знание Swift и SwiftUI', 'Опыт работы с криптографией'],
  },
  {
    id: 2,
    title: 'Senior Android Developer',
    department: 'Mobile Development',
    location: 'Удалённо',
    type: 'Полная занятость',
    description: 'Создание Android-клиента с фокусом на производительность и безопасность. Kotlin, Jetpack Compose, работа с протоколами шифрования.',
    requirements: ['5+ лет опыта Android разработки', 'Kotlin, Jetpack Compose', 'Понимание принципов безопасности'],
  },
  {
    id: 3,
    title: 'Backend Engineer (Rust/Go)',
    department: 'Backend',
    location: 'Удалённо',
    type: 'Полная занятость',
    description: 'Разработка высоконагруженных серверных компонентов. Работа с распределёнными системами, оптимизация производительности.',
    requirements: ['Опыт с Rust или Go', 'Знание распределённых систем', 'Опыт работы с высокими нагрузками'],
  },
  {
    id: 4,
    title: 'Product Designer',
    department: 'Design',
    location: 'Удалённо',
    type: 'Полная занятость',
    description: 'Проектирование пользовательского опыта для мобильных и десктопных приложений. Создание дизайн-системы и прототипов.',
    requirements: ['3+ лет опыта в продуктовом дизайне', 'Figma, протототипирование', 'Понимание мобильных платформ'],
  },
  {
    id: 5,
    title: 'DevOps Engineer',
    department: 'Infrastructure',
    location: 'Удалённо',
    type: 'Полная занятость',
    description: 'Построение и поддержка инфраструктуры. Kubernetes, CI/CD, мониторинг и обеспечение отказоустойчивости.',
    requirements: ['Kubernetes, Docker', 'Опыт с облачными провайдерами', 'Автоматизация и IaC'],
  },
];

const VacancyApplyModal = ({ isOpen, onClose, vacancy }) => {
  const [formData, setFormData] = useState({ name: '', email: '', telegram: '', resume: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await sendVacancyRequest({ vacancy: vacancy?.title || '', ...formData });
    setIsSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      onClose();
      setSubmitted(false);
      setFormData({ name: '', email: '', telegram: '', resume: '' });
    }, 2000);
  };

  if (!vacancy) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        data-testid="vacancy-modal"
        className="sm:max-w-md bg-zinc-950 border border-white/10 text-white"
      >
        <DialogHeader>
          <DialogTitle className="font-benzin text-xl">
            Отклик: {vacancy.title}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 flex flex-col items-center justify-center text-center" data-testid="vacancy-submit-success">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
              <Check className="w-6 h-6 text-white" />
            </div>
            <p className="text-white font-medium">Заявка отправлена</p>
            <p className="text-white/50 text-sm mt-1">Мы свяжемся с вами в ближайшее время.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="vac-name" className="text-white/70 text-sm">Имя</Label>
              <Input
                id="vac-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="vacancy-name-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vac-email" className="text-white/70 text-sm">Email</Label>
              <Input
                id="vac-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="vacancy-email-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vac-tg" className="text-white/70 text-sm">Telegram</Label>
              <Input
                id="vac-tg"
                value={formData.telegram}
                onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                placeholder="@username"
                required
                data-testid="vacancy-telegram-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vac-resume" className="text-white/70 text-sm">Резюме (ссылка)</Label>
              <Input
                id="vac-resume"
                value={formData.resume}
                onChange={(e) => setFormData({ ...formData, resume: e.target.value })}
                placeholder="hh.ru / LinkedIn / Notion / Drive"
                required
                data-testid="vacancy-resume-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="vacancy-submit-btn"
              className="w-full bg-white text-black hover:bg-white/90 font-medium py-5 transition-all duration-300 active:scale-[0.98]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Отправка...
                </>
              ) : (
                'Отправить отклик'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const VacancyCard = ({ vacancy, index, onClick }) => {
  return (
    <motion.button
      type="button"
      onClick={() => onClick(vacancy)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay: index * 0.08,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      data-testid={`vacancy-card-${vacancy.id}`}
      className="w-full text-left bg-white/[0.03] border border-white/10 rounded-xl p-6
                 transition-all duration-300 ease-out
                 hover:bg-white/[0.06] hover:border-white/20
                 focus:outline-none focus:ring-2 focus:ring-white/20
                 cursor-pointer"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-medium text-white mb-1 tracking-tight">
            {vacancy.title}
          </h3>
          <p className="text-white/40 text-sm">
            {vacancy.department}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-white/40">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            {vacancy.location}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {vacancy.type}
          </span>
        </div>
      </div>

      <p className="text-white/50 text-sm leading-relaxed mb-4">
        {vacancy.description}
      </p>

      <div className="flex flex-wrap gap-2">
        {vacancy.requirements.map((req, i) => (
          <span
            key={i}
            className="px-2.5 py-1 bg-white/5 text-white/50 text-xs rounded-md"
          >
            {req}
          </span>
        ))}
      </div>
    </motion.button>
  );
};

const Vacancies = () => {
  const [selectedVacancy, setSelectedVacancy] = useState(null);

  return (
    <div
      data-testid="vacancies-page"
      className="min-h-screen bg-black"
    >
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="back-to-home-btn"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Назад</span>
          </Link>
          <Link to="/">
            <SplaIcon size={28} className="text-white" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-10 pl-6"
        >
          <h1 className="font-benzin text-3xl text-white mb-3">
            Вакансии
          </h1>
          <p className="text-white/50 text-base leading-relaxed max-w-xl">
            Присоединяйтесь к команде Splav. Мы создаём мессенджер нового поколения
            и ищем талантливых специалистов.
          </p>
        </motion.div>

        <div className="space-y-3">
          {vacancies.map((vacancy, index) => (
            <VacancyCard
              key={vacancy.id}
              vacancy={vacancy}
              index={index}
              onClick={setSelectedVacancy}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 p-6 bg-white/[0.03] border border-white/10 rounded-xl text-center"
        >
          <p className="text-white/40 text-sm mb-1">
            Не нашли подходящую вакансию?
          </p>
          <p className="text-white/70 text-sm">
            Отправьте резюме на{' '}
            <a
              href="mailto:jobs@splav.app"
              data-testid="contact-email-link"
              className="text-white hover:text-white/80 underline underline-offset-4 transition-colors duration-300"
            >
              jobs@splav.app
            </a>
          </p>
        </motion.div>
      </main>

      <AnimatePresence>
        {selectedVacancy && (
          <VacancyApplyModal
            isOpen={!!selectedVacancy}
            onClose={() => setSelectedVacancy(null)}
            vacancy={selectedVacancy}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Vacancies;
